import { useState, useEffect, ReactNode } from 'react'
import type { Product, CartItem, Cart } from '../types'
import { CartContext } from '../hooks/useCart'

const EMPTY_CART: Cart = {
  items: [],
  total_excl_tax: 0,
  total_incl_tax: 0,
  total_vat: 0,
}

function getCurrentUserId(): string | null {
  try {
    const userStr = localStorage.getItem('user')
    if (userStr) {
      const user = JSON.parse(userStr)
      return user.id ? String(user.id) : null
    }
  } catch {
    // ignore parse errors
  }
  return null
}

function getCartKey(userId: string | null): string {
  return userId ? `cart_${userId}` : 'cart_anonymous'
}

function loadCartFromStorage(cartKey: string): Cart {
  try {
    const savedCart = localStorage.getItem(cartKey)
    if (savedCart) {
      const parsed = JSON.parse(savedCart)
      if (
        parsed &&
        Array.isArray(parsed.items) &&
        typeof parsed.total_excl_tax === 'number' &&
        typeof parsed.total_incl_tax === 'number' &&
        typeof parsed.total_vat === 'number'
      ) {
        return parsed
      }
    }
  } catch (error) {
    console.error('Error loading cart:', error)
  }
  localStorage.removeItem(cartKey)
  return { ...EMPTY_CART }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [userId, setUserId] = useState<string | null>(() => getCurrentUserId())
  const [cart, setCart] = useState<Cart>(() => loadCartFromStorage(getCartKey(getCurrentUserId())))

  // Reload cart when the user changes (login/logout), in the same tab or another tab
  useEffect(() => {
    const handleUserChange = () => {
      const newUserId = getCurrentUserId()
      setUserId((prevId: string | null) => {
        if (newUserId !== prevId) {
          setCart(loadCartFromStorage(getCartKey(newUserId)))
          return newUserId
        }
        return prevId
      })
    }

    // Cross-tab: native storage events
    const handleStorageEvent = (e: StorageEvent) => {
      if (e.key === 'user' || e.key === 'auth_token') {
        handleUserChange()
      }
    }

    // Same-tab: custom event dispatched by api.ts on login/logout
    window.addEventListener('storage', handleStorageEvent)
    window.addEventListener('userChanged', handleUserChange)
    return () => {
      window.removeEventListener('storage', handleStorageEvent)
      window.removeEventListener('userChanged', handleUserChange)
    }
  }, [])

  // Persist cart to user-specific localStorage key on every change
  useEffect(() => {
    localStorage.setItem(getCartKey(userId), JSON.stringify(cart))
  }, [cart, userId])

  const calculateTotals = (
    items: CartItem[]
  ): Pick<Cart, 'total_excl_tax' | 'total_incl_tax' | 'total_vat'> => {
    const total_excl_tax = items.reduce(
      (sum: number, item: CartItem) => sum + item.product.price * item.quantity,
      0
    )
    const total_incl_tax = items.reduce(
      (sum: number, item: CartItem) => sum + item.product.price_ttc * item.quantity,
      0
    )
    const total_vat = total_incl_tax - total_excl_tax
    return { total_excl_tax, total_incl_tax, total_vat }
  }

  const addToCart = (product: Product, quantity: number) => {
    setCart((prevCart) => {
      const existingItemIndex = prevCart.items.findIndex((item) => item.product.id === product.id)
      let newItems: CartItem[]

      if (existingItemIndex > -1) {
        newItems = [...prevCart.items]
        newItems[existingItemIndex].quantity += quantity
      } else {
        newItems = [...prevCart.items, { product, quantity }]
      }

      const totals = calculateTotals(newItems)
      return { items: newItems, ...totals }
    })
  }

  const removeFromCart = (productId: string) => {
    setCart((prevCart: Cart) => {
      const newItems = prevCart.items.filter((item: CartItem) => item.product.id !== productId)
      const totals = calculateTotals(newItems)
      return { items: newItems, ...totals }
    })
  }

  const updateQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId)
      return
    }

    setCart((prevCart) => {
      const newItems = prevCart.items.map((item: CartItem) =>
        item.product.id === productId ? { ...item, quantity } : item
      )
      const totals = calculateTotals(newItems)
      return { items: newItems, ...totals }
    })
  }

  const clearCart = () => {
    setCart({ ...EMPTY_CART })
  }

  const getItemCount = () => {
    return cart.items.reduce((sum, item) => sum + item.quantity, 0)
  }

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getItemCount,
      }}
    >
      {children}
    </CartContext.Provider>
  )
}
